import { RiskyDisclosureParagraph } from '@/components/common/DisclosureParagraph';
import FadeInBox from '@/components/common/FadeInBox';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import { InfoButtonWithModal } from '@/components/common/InfoModal';
import { Header, Paragraph, Text } from '@/components/common/Typography';
import { FlexProps, Grid } from '@chakra-ui/react';
import { getInvestmentType, getMoneyMarketRateStr } from '@common/react/goals';
import { useCurrentUser } from '@common/react/user';
import { isNumber } from '@common/util/types';
import { useState } from 'react';
import { configByGoalType } from '../../config';
import {
    getAnnualReturnInfoForRiskScore,
    getGoalDataFromExistingGoal,
} from '../../helpers';
import {
    PieChartSection,
    RiskSelectionContent,
    RiskSlider,
} from '../../RiskSelectionContent';
import { useGoalDetailContext } from '../GoalDetailContext';
import { useGoalOnboardingContext } from './GoalOnboardingContext';
import GoalOnboardingLayout from './GoalOnboardingLayout';

export interface GoalOnboardingPortfolioProps extends FlexProps {
    onBack?: () => void;
    onComplete: () => void;
}

const GoalOnboardingPortfolio = ({
    onBack,
    onComplete,
    ...props
}: GoalOnboardingPortfolioProps) => {
    const { goal, goalOnboardingData, updateGoalOnboarding, isUpdating } =
        useGoalOnboardingContext();
    const [riskScore, setRiskScore] = useState(goalOnboardingData.riskScore);
    const goalConfig = configByGoalType[goal.type];
    const { userInfo } = useCurrentUser();

    const recommendedRiskScore = goalConfig.getRecommendedRiskScore({
        goalData: getGoalDataFromExistingGoal(goal),
        userInfo,
    });

    const investmentType = getInvestmentType({
        riskScore,
        accountStructure: goal.accountStructure,
    });

    return (
        <GoalOnboardingLayout
            {...props}
            footer={{
                back: onBack ? { onBack } : undefined,
                secondary:
                    goalConfig.group === 'INVEST' &&
                    isNumber(recommendedRiskScore) &&
                    riskScore !== recommendedRiskScore
                        ? {
                              onClick: () => setRiskScore(recommendedRiskScore),
                              text: 'Reset to recommended',
                          }
                        : undefined,
                cta: {
                    onComplete: async () => {
                        await updateGoalOnboarding({
                            params: {
                                info: {
                                    investmentType,
                                    riskScore,
                                    recommendedRiskScore,
                                    lastCompletedStep: 'PORTFOLIO',
                                },
                            },
                        });
                        onComplete();
                    },
                    isSubmitting: isUpdating,
                },
            }}
        >
            <FadeInBox>
                {goalConfig.group === 'HIGH_YIELD_CASH_ONLY' ? (
                    // HIGH_YIELD_CASH_ONLY goals don't show a risk score slider
                    <LowestRiskContent />
                ) : (
                    // All other goals show a risk score slider
                    <RiskSelectionContent
                        title="Choose your investment mix"
                        riskScore={riskScore}
                        recommendedRiskScore={recommendedRiskScore}
                        // General Investing risk score must be 1-10
                        minRiskScore={goal.type === 'GENERAL_INVESTING' ? 1 : 0}
                        onChangeRiskScore={setRiskScore}
                        investmentType={investmentType}
                    />
                )}
            </FadeInBox>
        </GoalOnboardingLayout>
    );
};

const LowestRiskContent = () => {
    const { currentRateInfo } = useGoalDetailContext();
    const { goal } = useGoalOnboardingContext();

    const investmentType = 'HIGH_YIELD_CASH';
    const riskScore = 0;

    const { annualReturnLabel, annualReturnDisclosure } =
        getAnnualReturnInfoForRiskScore({
            investmentType,
            riskScore,
            currentRateInfo,
        });

    return (
        <>
            <FlexRow>
                <FlexCol grow={1} basis={0}>
                    <Header fontWeight="medium" mb={2}>
                        {`Your ‘${goal.name}’ portfolio`}
                    </Header>
                    <Paragraph color="textMuted">
                        Emergency funds should be easily and quickly accessible.
                        We use a money market fund to ensure your emergency fund
                        grows and is in one of the lowest risk investments
                        available. The money market fund we use is 99.5%
                        government backed treasuries and you can withdraw
                        anytime.
                    </Paragraph>
                    <Grid mt={10} templateColumns={'repeat(3, 1fr)'} gap={8}>
                        <FlexCol>
                            <Text fontSize="sm" color="textMuted" mb={2}>
                                Risk level
                            </Text>
                            <Header fontWeight="medium">Lowest</Header>
                        </FlexCol>
                        <FlexCol>
                            <Text fontSize="sm" color="textMuted" mb={2}>
                                {annualReturnLabel}
                                <InfoButtonWithModal
                                    buttonProps={{
                                        h: 4,
                                        w: 4,
                                        onClickEventParams: {
                                            location: 'Goal Onboarding',
                                            step: 'PORTFOLIO',
                                        },
                                    }}
                                    title={annualReturnLabel}
                                >
                                    {annualReturnDisclosure}
                                </InfoButtonWithModal>
                            </Text>
                            <Header fontWeight="medium">
                                {getMoneyMarketRateStr(currentRateInfo)}
                            </Header>
                        </FlexCol>
                    </Grid>
                </FlexCol>
                <PieChartSection
                    investmentType={investmentType}
                    riskScore={riskScore}
                />
            </FlexRow>

            <RiskSlider value={0.15} readOnly />
            <RiskyDisclosureParagraph mt={8} />
        </>
    );
};

export default GoalOnboardingPortfolio;
